import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Calculator, Clock, CheckCircle, AlertCircle, Eye, Upload, 
  Car, Calendar, User, FileText, ArrowLeft, Save, Download,
  RefreshCw, Search, Filter
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuperadminKalkulation = () => {
  const navigate = useNavigate();
  const [kalkulationen, setKalkulationen] = useState([]);
  const [stats, setStats] = useState({ total: 0, eingereicht: 0, in_bearbeitung: 0, abgeschlossen: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedKalkulation, setSelectedKalkulation] = useState(null);
  const [selectedAkte, setSelectedAkte] = useState(null);
  const [filterStatus, setFilterStatus] = useState('wartend');
  const [ergebnisForm, setErgebnisForm] = useState({
    reparaturkosten_netto: '',
    reparaturkosten_brutto: '',
    arbeitskosten: '',
    ersatzteilkosten: '',
    lackierkosten: '',
    reparaturdauer_stunden: '',
    reparaturdauer_tage: '',
    wiederbeschaffungswert: '',
    restwert: '',
    minderwert: '',
    mietwagenkosten_pro_tag: '',
    nutzungsausfall_pro_tag: '',
    totalschaden: false,
    wirtschaftlicher_totalschaden: false,
    bemerkungen: ''
  });
  const [uploadingPdf, setUploadingPdf] = useState(false);

  const getToken = () => localStorage.getItem('autogutachter24_token');

  useEffect(() => {
    loadKalkulationen();
  }, [filterStatus]);

  const loadKalkulationen = async () => {
    setLoading(true);
    try {
      const endpoint = filterStatus === 'wartend' 
        ? '/api/kalkulation/admin/warteschlange'
        : '/api/kalkulation/admin/alle';
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (!response.ok) throw new Error('Fehler beim Laden');
      
      const data = await response.json();
      setKalkulationen(data.kalkulationen || []);
      if (data.stats) setStats(data.stats);
    } catch (error) {
      toast.error('Fehler beim Laden der Kalkulationen');
    } finally {
      setLoading(false);
    }
  };

  const loadKalkulationDetail = async (kalkulationId) => {
    try {
      const response = await fetch(`${API_URL}/api/kalkulation/admin/detail/${kalkulationId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (!response.ok) throw new Error('Fehler beim Laden');
      
      const data = await response.json();
      setSelectedKalkulation(data.kalkulation);
      setSelectedAkte(data.akte);
      
      // Fülle Ergebnis-Form wenn vorhanden
      if (data.kalkulation.ergebnis) {
        setErgebnisForm(prev => ({
          ...prev,
          ...data.kalkulation.ergebnis
        }));
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Details');
    }
  };

  const startBearbeitung = async (kalkulationId) => {
    try {
      const response = await fetch(`${API_URL}/api/kalkulation/admin/bearbeiten/${kalkulationId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (!response.ok) throw new Error('Fehler');
      
      toast.success('Bearbeitung gestartet');
      loadKalkulationen();
      loadKalkulationDetail(kalkulationId);
    } catch (error) {
      toast.error('Fehler beim Starten der Bearbeitung');
    }
  };

  const uploadPdf = async (kalkulationId, file) => {
    setUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/kalkulation/admin/upload-pdf/${kalkulationId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formData
      });
      
      if (!response.ok) throw new Error('Upload fehlgeschlagen');
      
      toast.success('PDF hochgeladen');
      loadKalkulationDetail(kalkulationId);
    } catch (error) {
      toast.error('Fehler beim Upload');
    } finally {
      setUploadingPdf(false);
    }
  };

  const abschliessenKalkulation = async () => {
    if (!selectedKalkulation) return;
    
    try {
      // Konvertiere String-Werte zu Zahlen
      const ergebnis = {
        ...ergebnisForm,
        reparaturkosten_netto: ergebnisForm.reparaturkosten_netto ? parseFloat(ergebnisForm.reparaturkosten_netto) : null,
        reparaturkosten_brutto: ergebnisForm.reparaturkosten_brutto ? parseFloat(ergebnisForm.reparaturkosten_brutto) : null,
        arbeitskosten: ergebnisForm.arbeitskosten ? parseFloat(ergebnisForm.arbeitskosten) : null,
        ersatzteilkosten: ergebnisForm.ersatzteilkosten ? parseFloat(ergebnisForm.ersatzteilkosten) : null,
        lackierkosten: ergebnisForm.lackierkosten ? parseFloat(ergebnisForm.lackierkosten) : null,
        reparaturdauer_stunden: ergebnisForm.reparaturdauer_stunden ? parseFloat(ergebnisForm.reparaturdauer_stunden) : null,
        reparaturdauer_tage: ergebnisForm.reparaturdauer_tage ? parseInt(ergebnisForm.reparaturdauer_tage) : null,
        wiederbeschaffungswert: ergebnisForm.wiederbeschaffungswert ? parseFloat(ergebnisForm.wiederbeschaffungswert) : null,
        restwert: ergebnisForm.restwert ? parseFloat(ergebnisForm.restwert) : null,
        minderwert: ergebnisForm.minderwert ? parseFloat(ergebnisForm.minderwert) : null,
        mietwagenkosten_pro_tag: ergebnisForm.mietwagenkosten_pro_tag ? parseFloat(ergebnisForm.mietwagenkosten_pro_tag) : null,
        nutzungsausfall_pro_tag: ergebnisForm.nutzungsausfall_pro_tag ? parseFloat(ergebnisForm.nutzungsausfall_pro_tag) : null,
      };
      
      const response = await fetch(`${API_URL}/api/kalkulation/admin/abschliessen/${selectedKalkulation.id}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ergebnis)
      });
      
      if (!response.ok) throw new Error('Fehler beim Abschließen');
      
      toast.success('Kalkulation abgeschlossen!');
      setSelectedKalkulation(null);
      setSelectedAkte(null);
      loadKalkulationen();
    } catch (error) {
      toast.error('Fehler beim Abschließen der Kalkulation');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'eingereicht': { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Wartend' },
      'in_bearbeitung': { bg: 'bg-blue-100', text: 'text-blue-800', icon: RefreshCw, label: 'In Bearbeitung' },
      'abgeschlossen': { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Abgeschlossen' },
      'abgelehnt': { bg: 'bg-red-100', text: 'text-red-800', icon: AlertCircle, label: 'Abgelehnt' }
    };
    const badge = badges[status] || badges['eingereicht'];
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
  };

  // Detail-Ansicht
  if (selectedKalkulation && selectedAkte) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => { setSelectedKalkulation(null); setSelectedAkte(null); }}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück zur Liste
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">
                Kalkulation: {selectedKalkulation.aktenzeichen}
              </h1>
              {getStatusBadge(selectedKalkulation.status)}
            </div>
            
            {selectedKalkulation.status !== 'abgeschlossen' && (
              <div className="flex space-x-3">
                {selectedKalkulation.status === 'eingereicht' && (
                  <Button onClick={() => startBearbeitung(selectedKalkulation.id)} className="bg-blue-600">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Bearbeitung starten
                  </Button>
                )}
                {selectedKalkulation.status === 'in_bearbeitung' && (
                  <Button onClick={abschliessenKalkulation} className="bg-green-600">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Kalkulation abschließen
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Linke Spalte: Akte-Daten */}
            <div className="space-y-6">
              {/* Fahrzeugdaten */}
              <Card className="border border-gray-100 shadow-sm rounded-xl">
                <CardHeader className="bg-blue-50 border-b border-blue-100 px-6 py-4">
                  <CardTitle className="flex items-center text-blue-900">
                    <Car className="w-5 h-5 mr-2" />
                    Fahrzeugdaten
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Marke/Modell</span>
                      <p className="font-medium">{selectedAkte?.marke} {selectedAkte?.modell}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Kennzeichen</span>
                      <p className="font-medium">{selectedAkte?.kennzeichen || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Erstzulassung</span>
                      <p className="font-medium">{selectedAkte?.erstzulassung || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Kilometerstand</span>
                      <p className="font-medium">{selectedAkte?.kilometerstand ? `${selectedAkte.kilometerstand} km` : '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">VIN</span>
                      <p className="font-medium font-mono text-xs">{selectedAkte?.vin || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Schadentag</span>
                      <p className="font-medium">{selectedAkte?.schadentag || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Auftraggeber */}
              <Card className="border border-gray-100 shadow-sm rounded-xl">
                <CardHeader className="bg-purple-50 border-b border-purple-100 px-6 py-4">
                  <CardTitle className="flex items-center text-purple-900">
                    <User className="w-5 h-5 mr-2" />
                    Auftraggeber
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="col-span-2">
                      <span className="text-gray-500">Name</span>
                      <p className="font-medium">{selectedAkte?.kunde || '-'}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">Adresse</span>
                      <p className="font-medium">{selectedAkte?.adresse1 || '-'}</p>
                      <p className="font-medium">{selectedAkte?.adresse2 || ''}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Bilder */}
              <Card className="border border-gray-100 shadow-sm rounded-xl">
                <CardHeader className="bg-orange-50 border-b border-orange-100 px-6 py-4">
                  <CardTitle className="flex items-center text-orange-900">
                    <FileText className="w-5 h-5 mr-2" />
                    Lichtbilder ({selectedAkte?.lichtbilder?.length || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {selectedAkte?.lichtbilder?.length > 0 ? (
                    <div className="grid grid-cols-3 gap-3">
                      {selectedAkte.lichtbilder.slice(0, 9).map((bild, idx) => (
                        <div key={idx} className="aspect-square rounded-lg overflow-hidden border border-gray-200">
                          <img 
                            src={bild.url || bild.thumbnail_url || bild} 
                            alt={`Bild ${idx + 1}`}
                            className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer"
                            onClick={() => window.open(bild.url || bild, '_blank')}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">Keine Bilder vorhanden</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Rechte Spalte: Kalkulations-Eingabe */}
            <div className="space-y-6">
              {/* PDF Upload */}
              <Card className="border border-gray-100 shadow-sm rounded-xl">
                <CardHeader className="bg-green-50 border-b border-green-100 px-6 py-4">
                  <CardTitle className="flex items-center text-green-900">
                    <Upload className="w-5 h-5 mr-2" />
                    Kalkulations-PDF
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {selectedKalkulation.pdf_url ? (
                    <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <FileText className="w-8 h-8 text-green-600" />
                        <div>
                          <p className="font-medium">{selectedKalkulation.pdf_filename}</p>
                          <p className="text-sm text-gray-500">PDF hochgeladen</p>
                        </div>
                      </div>
                      <Button 
                        variant="outline"
                        onClick={() => window.open(`${API_URL}${selectedKalkulation.pdf_url}`, '_blank')}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center">
                      <input
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        id="pdf-upload"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            uploadPdf(selectedKalkulation.id, e.target.files[0]);
                          }
                        }}
                      />
                      <label htmlFor="pdf-upload" className="cursor-pointer">
                        <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                        <p className="font-medium text-gray-700">Kalkulations-PDF hochladen</p>
                        <p className="text-sm text-gray-500 mt-1">Klicken oder Datei hierher ziehen</p>
                      </label>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Ergebnis-Eingabe */}
              <Card className="border border-gray-100 shadow-sm rounded-xl">
                <CardHeader className="bg-blue-50 border-b border-blue-100 px-6 py-4">
                  <CardTitle className="flex items-center text-blue-900">
                    <Calculator className="w-5 h-5 mr-2" />
                    Kalkulations-Ergebnis
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  {/* Reparaturkosten */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Reparaturkosten</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Netto (€)</label>
                        <Input
                          type="number"
                          value={ergebnisForm.reparaturkosten_netto}
                          onChange={(e) => setErgebnisForm(prev => ({ ...prev, reparaturkosten_netto: e.target.value }))}
                          placeholder="0.00"
                          className="h-10"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Brutto (€)</label>
                        <Input
                          type="number"
                          value={ergebnisForm.reparaturkosten_brutto}
                          onChange={(e) => setErgebnisForm(prev => ({ ...prev, reparaturkosten_brutto: e.target.value }))}
                          placeholder="0.00"
                          className="h-10"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Arbeitskosten (€)</label>
                        <Input
                          type="number"
                          value={ergebnisForm.arbeitskosten}
                          onChange={(e) => setErgebnisForm(prev => ({ ...prev, arbeitskosten: e.target.value }))}
                          placeholder="0.00"
                          className="h-10"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Ersatzteile (€)</label>
                        <Input
                          type="number"
                          value={ergebnisForm.ersatzteilkosten}
                          onChange={(e) => setErgebnisForm(prev => ({ ...prev, ersatzteilkosten: e.target.value }))}
                          placeholder="0.00"
                          className="h-10"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Lackierkosten (€)</label>
                        <Input
                          type="number"
                          value={ergebnisForm.lackierkosten}
                          onChange={(e) => setErgebnisForm(prev => ({ ...prev, lackierkosten: e.target.value }))}
                          placeholder="0.00"
                          className="h-10"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Zeiten */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Reparaturdauer</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Stunden</label>
                        <Input
                          type="number"
                          value={ergebnisForm.reparaturdauer_stunden}
                          onChange={(e) => setErgebnisForm(prev => ({ ...prev, reparaturdauer_stunden: e.target.value }))}
                          placeholder="0"
                          className="h-10"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Tage</label>
                        <Input
                          type="number"
                          value={ergebnisForm.reparaturdauer_tage}
                          onChange={(e) => setErgebnisForm(prev => ({ ...prev, reparaturdauer_tage: e.target.value }))}
                          placeholder="0"
                          className="h-10"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Werte */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Fahrzeugwerte</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Wiederbeschaffungswert (€)</label>
                        <Input
                          type="number"
                          value={ergebnisForm.wiederbeschaffungswert}
                          onChange={(e) => setErgebnisForm(prev => ({ ...prev, wiederbeschaffungswert: e.target.value }))}
                          placeholder="0.00"
                          className="h-10"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Restwert (€)</label>
                        <Input
                          type="number"
                          value={ergebnisForm.restwert}
                          onChange={(e) => setErgebnisForm(prev => ({ ...prev, restwert: e.target.value }))}
                          placeholder="0.00"
                          className="h-10"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Minderwert (€)</label>
                        <Input
                          type="number"
                          value={ergebnisForm.minderwert}
                          onChange={(e) => setErgebnisForm(prev => ({ ...prev, minderwert: e.target.value }))}
                          placeholder="0.00"
                          className="h-10"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Zusätzliche Kosten */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Zusätzliche Kosten</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Mietwagen/Tag (€)</label>
                        <Input
                          type="number"
                          value={ergebnisForm.mietwagenkosten_pro_tag}
                          onChange={(e) => setErgebnisForm(prev => ({ ...prev, mietwagenkosten_pro_tag: e.target.value }))}
                          placeholder="0.00"
                          className="h-10"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Nutzungsausfall/Tag (€)</label>
                        <Input
                          type="number"
                          value={ergebnisForm.nutzungsausfall_pro_tag}
                          onChange={(e) => setErgebnisForm(prev => ({ ...prev, nutzungsausfall_pro_tag: e.target.value }))}
                          placeholder="0.00"
                          className="h-10"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Totalschaden */}
                  <div className="flex items-center space-x-6">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={ergebnisForm.totalschaden}
                        onChange={(e) => setErgebnisForm(prev => ({ ...prev, totalschaden: e.target.checked }))}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm font-medium text-gray-700">Totalschaden</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={ergebnisForm.wirtschaftlicher_totalschaden}
                        onChange={(e) => setErgebnisForm(prev => ({ ...prev, wirtschaftlicher_totalschaden: e.target.checked }))}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm font-medium text-gray-700">Wirtschaftlicher Totalschaden</span>
                    </label>
                  </div>

                  {/* Bemerkungen */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Bemerkungen</label>
                    <textarea
                      value={ergebnisForm.bemerkungen}
                      onChange={(e) => setErgebnisForm(prev => ({ ...prev, bemerkungen: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      rows={3}
                      placeholder="Optionale Bemerkungen zur Kalkulation..."
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Listen-Ansicht
  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Kalkulations-Verwaltung</h1>
            <p className="text-gray-500 mt-1">Superadmin-Bereich für eingereichte Kalkulationen</p>
          </div>
          <Button onClick={loadKalkulationen} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
        </div>

        {/* Statistiken */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="border border-yellow-100 bg-yellow-50">
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <Clock className="w-8 h-8 text-yellow-600" />
                <div>
                  <p className="text-2xl font-bold text-yellow-900">{stats.eingereicht}</p>
                  <p className="text-sm text-yellow-700">Wartend</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-blue-100 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <RefreshCw className="w-8 h-8 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold text-blue-900">{stats.in_bearbeitung}</p>
                  <p className="text-sm text-blue-700">In Bearbeitung</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-green-100 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-8 h-8 text-green-600" />
                <div>
                  <p className="text-2xl font-bold text-green-900">{stats.abgeschlossen}</p>
                  <p className="text-sm text-green-700">Abgeschlossen</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-100 bg-gray-50">
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <Calculator className="w-8 h-8 text-gray-600" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                  <p className="text-sm text-gray-700">Gesamt</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex space-x-2">
          <Button 
            variant={filterStatus === 'wartend' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('wartend')}
          >
            Warteschlange
          </Button>
          <Button 
            variant={filterStatus === 'alle' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('alle')}
          >
            Alle anzeigen
          </Button>
        </div>

        {/* Tabelle */}
        <Card className="border border-gray-100 shadow-sm rounded-xl">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-3" />
                <p className="text-gray-500">Laden...</p>
              </div>
            ) : kalkulationen.length === 0 ? (
              <div className="p-8 text-center">
                <Calculator className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Keine Kalkulationen vorhanden</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Aktenzeichen</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Fahrzeug</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Gutachter</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Eingereicht</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Bilder</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Aktion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {kalkulationen.map((kalk) => (
                    <tr key={kalk.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <span className="font-medium text-gray-900">{kalk.aktenzeichen}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium">{kalk.fahrzeug_marke} {kalk.fahrzeug_modell}</p>
                          <p className="text-xs text-gray-500">{kalk.kennzeichen}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{kalk.user_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{formatDate(kalk.eingereicht_am)}</td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
                          {kalk.bilder_count} Bilder
                        </span>
                      </td>
                      <td className="px-6 py-4">{getStatusBadge(kalk.status)}</td>
                      <td className="px-6 py-4 text-right">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => loadKalkulationDetail(kalk.id)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default SuperadminKalkulation;
